import React, { useMemo, useState } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { WidgetCard, WidgetEmptyState } from './WidgetCard';
import { buildSeriesFromRows, filterRowsByWindow } from './widgetData';

const normalizeSeries = (series) => {
  if (!series.length) return series;
  const length = series[0].data.length;
  const totals = Array.from({ length }, (_, index) =>
    series.reduce((sum, entry) => sum + (entry.data[index] || 0), 0)
  );
  return series.map((entry) => ({
    ...entry,
    data: entry.data.map((value, index) => (totals[index] ? (value / totals[index]) * 100 : 0)),
  }));
};

const formatWindowLabel = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  if (/^\\d+d$/i.test(raw) || /^\\d+y$/i.test(raw)) return raw.toLowerCase();
  if (/last_7_days/i.test(raw)) return '7d';
  if (/last_30_days/i.test(raw)) return '30d';
  if (/last_90_days/i.test(raw)) return '90d';
  if (/last_365_days/i.test(raw)) return '1y';
  return raw;
};

const StackedBarWidget = ({ widget, rows, normalize = false }) => {
  const { xField, seriesField, valueField, title, limit, showLegend, window, windowField, windows, defaultWindow } =
    widget.params || {};
  const windowOptions = useMemo(() => (Array.isArray(windows) ? windows.filter(Boolean) : []), [windows]);
  const resolvedDefault = defaultWindow || window || windowOptions[0] || null;
  const [activeWindow, setActiveWindow] = useState(resolvedDefault);
  const effectiveWindow = windowOptions.length ? activeWindow : window;
  const scopedRows = filterRowsByWindow({ rows, field: windowField || xField, window: effectiveWindow });
  const { groups, series } = buildSeriesFromRows({
    rows: scopedRows,
    groupField: xField,
    seriesField,
    valueField,
    limit,
  });

  const windowToggle = windowOptions.length > 1 ? (
    <ToggleButtonGroup
      size="small"
      color="primary"
      exclusive
      value={activeWindow || ''}
      onChange={(_event, nextValue) => {
        if (nextValue) setActiveWindow(nextValue);
      }}
      aria-label="window selector"
    >
      {windowOptions.map((option) => (
        <ToggleButton key={option} value={option} aria-label={formatWindowLabel(option)}>
          {formatWindowLabel(option)}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  ) : null;

  if (!groups.length || !series.length) {
    return (
      <WidgetCard
        title={title || (normalize ? 'Percent Stacked Bar' : 'Stacked Bar')}
        subtitle={seriesField}
        action={windowToggle}
        testId={`report-widget-${widget.id || widget.type}`}
      >
        <WidgetEmptyState message="Stacked bar data is not available yet." />
      </WidgetCard>
    );
  }

  const resolvedSeries = normalize ? normalizeSeries(series) : series;
  return (
    <WidgetCard
      title={title || (normalize ? 'Percent Stacked Bar' : 'Stacked Bar')}
      subtitle={seriesField}
      action={windowToggle}
      testId={`report-widget-${widget.id || widget.type}`}
    >
      <BarChart
        height={240}
        xAxis={[{ data: groups, scaleType: 'band' }]}
        series={resolvedSeries.map((entry) => ({
          data: entry.data,
          label: entry.label,
          stack: 'total',
        }))}
        legend={{ hidden: !showLegend }}
      />
    </WidgetCard>
  );
};

export default StackedBarWidget;
